import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskCompletion, TaskCompletionScopeNameEnum } from 'graphql-common';
import { useTaskCompletionsIdsLazyQuery } from '@lib/graphql-common';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { logger } from '@lib/services/logger';

/**
 * The intervention create rejects the whole selection if any completion isn't
 * one an intervention may still cover (success, resolved, or already
 * covered), so narrow it through the same backend policy scope before it
 * reaches the form. Resolves to `null` when the check itself failed (the
 * error is already surfaced) — callers must not proceed then.
 */
export default function useEligibleCompletionsForIntervention(
  groupKey?: string | null,
) {
  const { t } = useTranslation();
  const [fetchEligibleCompletionIdsQuery] = useTaskCompletionsIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  return useCallback(
    async <T extends Pick<TaskCompletion, 'id'>>(
      items: T[],
    ): Promise<T[] | null> => {
      if (!items.length) return items;
      try {
        const { data, error } = await fetchEligibleCompletionIdsQuery({
          variables: {
            groupKey: groupKey || '',
            scope: TaskCompletionScopeNameEnum.EligibleForIntervention,
            limit: items.length,
            filters: [
              { id: { predicate: { in: items.map((item) => item.id) } } },
            ],
          },
        });
        if (error) throw error;
        // A null resolver is not "nothing eligible" — don't report every
        // completion as ineligible on a response we can't read.
        const collection = data?.data?.collection;
        if (!collection) throw new Error('taskCompletions returned no data');
        const eligibleIds = new Set(collection.map(({ id }) => id));
        const eligible = items.filter((item) => eligibleIds.has(item.id));
        const leftOutCount = items.length - eligible.length;
        if (leftOutCount) {
          logger.info('TaskCompletions: ineligible completions left out', {
            groupKey,
            requested: items.length,
            eligible: eligible.length,
          });
          toast({
            content: t('completions-not-eligible-for-intervention', {
              count: leftOutCount,
            }),
          });
        }
        return eligible;
      } catch (error) {
        showGraphQLErrors(error, t);
        return null;
      }
    },
    [fetchEligibleCompletionIdsQuery, groupKey, t],
  );
}
